import React, { useState, useRef, useReducer } from "react";
import { Icon, Button } from "semantic-ui-react";
import ReactDOM from "react-dom";

import SemanticUiModal from "../modal";
import ToolTipList from "./tool_tip_list";
import ToolTipVideoModal from "./tool_tip_video_modal";
import ToolTipPopup from "./toolTipPopUp";

import "./tool_tip.css";

const initialSize = { width: 500, height: 200 };

const toolTipModalInitialState = {
  view: "CLOSED",
  videoSrc: null,
  videoTitle: null,
  from: null,
};

function toolTipModalReducer(state, action) {
  switch (action.type) {
    case "OPEN_POPUP":
      return { ...state, view: "POPUP" };
    case "CLOSE_POPUP":
      return { ...state, view: "CLOSED" };
    case "OPEN_ALL_TIPS":
      return { ...state, view: "ALL_TIPS" };
    case "CLOSE_ALL_TIPS":
      return { ...state, view: "POPUP" };
    case "OPEN_VIDEO":
      return {
        ...state,
        view: "VIDEO",
        videoSrc: action.payload.videoLink,
        videoTitle: action.payload.title,
        from: action.payload.from,
      };
    case "CLOSE_VIDEO":
      const returnToView =
        state.from === "popup"
          ? "POPUP"
          : state.from === "allTooltips"
          ? "ALL_TIPS"
          : "CLOSED";
      return {
        ...toolTipModalInitialState,
        view: returnToView,
      };
    default:
      return state;
  }
}

const ToolTip = ({
  title = "Help",
  description = "No description for this yet",
  video = null,
  size: iconSize = "tiny",
}) => {
  const [modalState, dispatch] = useReducer(toolTipModalReducer, toolTipModalInitialState);

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState(initialSize);
  const [isResized, setIsResized] = useState(false);
  const [isShifted, setIsShifted] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const triggerRef = useRef();
  const initialResizeData = useRef(null);

  const handleIconClick = () => {
    if (modalState.view === "POPUP") {
      handleClosePopup();
    } else {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const popupWidth = initialSize.width;
        const windowWidth = window.innerWidth;
        const popupHeight = initialSize.height;
        const windowHeight = window.innerHeight;

        let x = rect.left;
        let y = rect.bottom + 5;
        let shifted = false;
        let flipped = false;

        if (rect.left + popupWidth > windowWidth) {
          x = rect.right - popupWidth;
          shifted = true;
        }
        if (x < 0) x = 10;

        if (rect.bottom + popupHeight > windowHeight) {
          y = rect.top - popupHeight - 5;
          flipped = true;
        }

        setIsShifted(shifted);
        setIsFlipped(flipped);
        setPosition({ x, y });
      }
      setIsResized(false);
      setSize(initialSize);
      dispatch({ type: "OPEN_POPUP" });
    }
  };

  const handleClosePopup = () => {
    dispatch({ type: "CLOSE_POPUP" });
    setSize(initialSize);
    setIsResized(false);
  };

  const handleResizeStart = () => {
    initialResizeData.current = { size, position };
    setIsResized(true);
  };

  const handleResizeStop = () => {};

  const handleResize = (e, direction, ref, delta) => {
    if (!initialResizeData.current) return;

    const { size: initialSizeOnDrag, position: initialPosOnDrag } =
      initialResizeData.current;

    const newWidth = initialSizeOnDrag.width + delta.width;
    const newHeight = initialSizeOnDrag.height + delta.height;
    setSize({ width: newWidth, height: newHeight });

    let newX = initialPosOnDrag.x;
    let newY = initialPosOnDrag.y;

    if (direction.includes("left")) {
      newX = initialPosOnDrag.x - delta.width;
    }
    if (direction.includes("top")) {
      newY = initialPosOnDrag.y - delta.height;
    }
    setPosition({ x: newX, y: newY });
  };

  const openVideoModal = (videoLink, title, from) => {
    dispatch({ type: "OPEN_VIDEO", payload: { videoLink, title, from } });
  };

  const closeVideoModal = () => {
    dispatch({ type: "CLOSE_VIDEO" });
  };

  const openAllToolTipsModal = () => {
    dispatch({ type: "OPEN_ALL_TIPS" });
  };

  const closeAllToolTipsModal = () => {
    dispatch({ type: "CLOSE_ALL_TIPS" });
  };

  const sizeMap = {
    mini: '12px',
    tiny: '14px',
    small: '16px',
    medium: '20px',
    large: '24px',
    big: '28px',
    huge: '32px',
    massive: '36px',
  };
  const mappedIconSize = sizeMap[iconSize] || iconSize;

  return (
    <div className="tooltip-container">
      <button
        type="button"
        ref={triggerRef}
        onClick={handleIconClick}
        className="tooltip-trigger"
        aria-label={`Open tooltip for ${title}`}
      >
        <img src="/tool-tip-icon.svg" alt="Tool Tip" style={{ width: mappedIconSize, height: mappedIconSize }} className="tooltip-icon" />
      </button>

      {modalState.view === "POPUP" &&
        ReactDOM.createPortal(
          <ToolTipPopup
            onClose={handleClosePopup}
            position={position}
            size={size}
            onResizeStart={handleResizeStart}
            onResize={handleResize}
            onResizeStop={handleResizeStop}
            isResized={isResized}
            isShifted={isShifted}
            isFlipped={isFlipped}
            minWidth={initialSize.width}
            minHeight={initialSize.height}
          >
            <Icon
              name="close"
              className="tooltip-close-btn"
              onClick={handleClosePopup}
            />
            <div className="tooltip-header">
              <div className="tooltip-title">Tool Tip - {title}</div>
              <div className="tooltip-buttons">
                {video && (
                  <button
                    onClick={() => openVideoModal(video, title, "popup")}
                    className="tooltip-video-button"
                  >
                    Video
                  </button>
                )}
                <button
                  onClick={openAllToolTipsModal}
                  className="tooltip-link-button"
                >
                  All Tool Tips
                </button>
              </div>
            </div>
            <div className="tooltip-content-wrapper">
              <div className="tooltip-body">{description}</div>
            </div>
          </ToolTipPopup>,
          document.body
        )
    }

      <ToolTipVideoModal
        open={modalState.view === "VIDEO"}
        title={modalState.videoTitle}
        videoSrc={modalState.videoSrc || "No Video Source"}
        onClose={closeVideoModal}
      />

      {modalState.view === "ALL_TIPS" && (
        <SemanticUiModal
          modalOptions={{ size: "medium", closeOnDimmerClick: false }}
          header="All Tool Tips"
          content={
            <ToolTipList
              onOpenVideo={(videoLink, videoTitle) =>
                openVideoModal(videoLink, videoTitle, "allTooltips")
              }
            />
          }
          actions={
            <Button onClick={closeAllToolTipsModal} primary>
              Close
            </Button>
          }
          closeModal={closeAllToolTipsModal}
        />
      )}
    </div>
  );
};

export default ToolTip;


// WEBPACK FOOTER //
// ./src/components/tool_tips/tool_tip.js